/*
* Broadcast Theme
*
* Use this file to add custom Javascript to Broadcast.  Keeping your custom
* Javascript in this fill will make it easier to update Broadcast. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/


(function() {
  // Add custom code below this line

  /*
   * Custom cart drawer fixes.
   * Owns subtotal formatting after Broadcast's AJAX cart refresh rewrites
   * [data-cart-total]. Safe to run repeatedly because it observes and formats
   * only the current cart total node using theme.moneyFormat.
   */
  var formatCartSubtotal = function(total) {
    if (!total || !window.theme || !window.theme.formatMoney || !window.theme.moneyFormat) {
      return;
    }

    var subtotal = parseInt(total.getAttribute('data-cart-total'), 10);
    if (Number.isNaN(subtotal)) {
      return;
    }

    var formattedSubtotal = subtotal === 0 ? window.theme.strings.free : window.theme.formatMoney(subtotal, window.theme.moneyFormat);

    if (total.innerHTML !== formattedSubtotal) {
      total.innerHTML = formattedSubtotal;
    }
  };

  var formatCartSubtotals = function() {
    document.querySelectorAll('[data-cart-total]').forEach(formatCartSubtotal);
  };

  var observeCartSubtotal = function() {
    formatCartSubtotals();

    var observer = new MutationObserver(function(mutations) {
      var shouldFormat = mutations.some(function(mutation) {
        return mutation.type === 'childList' || mutation.type === 'attributes';
      });

      if (shouldFormat) {
        window.requestAnimationFrame(formatCartSubtotals);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-cart-total']
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCartSubtotal);
  } else {
    observeCartSubtotal();
  }

  class CustomCartAccessoryUpsells extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.updateControls = this.updateControls.bind(this);
      this.refreshRecommendations = this.refreshRecommendations.bind(this);
    }

    connectedCallback() {
      this.list = this.querySelector('[data-custom-cart-accessory-list]');
      this.previousButton = this.querySelector('[data-custom-cart-accessory-prev]');
      this.nextButton = this.querySelector('[data-custom-cart-accessory-next]');

      if (!this.list || !this.previousButton || !this.nextButton) return;

      this.fallbackCards = [...this.list.querySelectorAll('.custom-cart-accessory-upsells__item')].map((card) => card.cloneNode(true));
      this.addEventListener('click', this.onClick);
      this.list.addEventListener('scroll', this.updateControls, { passive: true });
      document.addEventListener('theme:cart:change', this.refreshRecommendations);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateControls);
        this.resizeObserver.observe(this.list);
        this.querySelectorAll('.custom-cart-accessory-upsells__item').forEach((item) => this.resizeObserver.observe(item));
      } else {
        window.addEventListener('resize', this.updateControls);
      }

      this.updateControls();
      this.refreshRecommendations();
    }

    disconnectedCallback() {
      this.recommendationsController?.abort();
      this.removeEventListener('click', this.onClick);
      this.list?.removeEventListener('scroll', this.updateControls);
      document.removeEventListener('theme:cart:change', this.refreshRecommendations);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.updateControls);
    }

    onClick(event) {
      const button = event.target.closest('[data-custom-cart-accessory-prev], [data-custom-cart-accessory-next]');
      if (!button || !this.contains(button)) return;

      const direction = button === this.nextButton ? 1 : -1;
      const current = this.list.scrollLeft;
      const listLeft = this.list.getBoundingClientRect().left;
      const positions = Array.from(this.list.querySelectorAll('.custom-cart-accessory-upsells__item'), (item) =>
        item.getBoundingClientRect().left - listLeft + current
      );
      const target = direction > 0
        ? positions.find((position) => position > current + 1)
        : positions.reverse().find((position) => position < current - 1);

      if (target === undefined) return;

      this.list.scrollTo({
        left: target,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    async refreshRecommendations() {
      this.recommendationsController?.abort();
      const controller = new AbortController();
      this.recommendationsController = controller;
      let productIds = [...new Set((this.dataset.customCartAccessoryProductIds || '').split(',').filter(Boolean))];

      try {
        const cartUrl = new URL(this.dataset.customCartAccessoryCartUrl, window.location.origin);
        cartUrl.pathname += '.js';
        const response = await fetch(cartUrl.toString(), { signal: controller.signal });
        if (response.ok) {
          const currentCart = await response.json();
          productIds = [...new Set(currentCart.items.map((item) => String(item.product_id)))];
        }
      } catch (_) {
        // Use the cart state from Liquid if the live cart request fails.
      }

      if (!this.isConnected || controller.signal.aborted) return;

      const cartProductIds = new Set(productIds);
      if (this.dataset.customCartAccessorySource !== 'automatic') {
        this.renderFallback(cartProductIds);
        return;
      }

      if (!productIds.length) {
        this.renderCards([]);
        return;
      }

      const recommendationsUrl = this.dataset.customCartAccessoryRecommendationsUrl;
      if (!recommendationsUrl) {
        this.renderFallback(cartProductIds);
        return;
      }

      const responses = await Promise.all(productIds.map(async (productId) => {
        const url = new URL(recommendationsUrl, window.location.origin);
        url.searchParams.set('section_id', 'custom-cart-upsell-recommendations');
        url.searchParams.set('product_id', productId);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'related');

        try {
          const response = await fetch(url.toString(), { signal: controller.signal });
          if (!response.ok) return [];

          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          return [...html.querySelectorAll('[data-custom-cart-accessory-product-id]')];
        } catch (_) {
          return [];
        }
      }));

      if (!this.isConnected || controller.signal.aborted) return;

      const seen = new Set(productIds);
      const cards = [];
      for (const recommendations of responses) {
        for (const card of recommendations) {
          const productId = card.dataset.customCartAccessoryProductId;
          if (!productId || seen.has(productId)) continue;
          seen.add(productId);
          cards.push(card);
          if (cards.length === 6) break;
        }
        if (cards.length === 6) break;
      }

      if (cards.length) {
        this.renderCards(cards);
      } else {
        this.renderFallback(cartProductIds);
      }
    }

    renderFallback(cartProductIds) {
      const cards = this.fallbackCards
        .filter((card) => !cartProductIds.has(card.dataset.customCartAccessoryProductId))
        .map((card) => card.cloneNode(true));
      this.renderCards(cards);
    }

    renderCards(cards) {
      this.resizeObserver?.disconnect();
      this.list.replaceChildren(...cards);
      this.list.scrollLeft = 0;
      this.hidden = cards.length === 0;

      if (this.resizeObserver) {
        this.resizeObserver.observe(this.list);
        cards.forEach((card) => this.resizeObserver.observe(card));
      }

      this.updateControls();
    }

    updateControls() {
      const maxScroll = this.list.scrollWidth - this.list.clientWidth;
      this.previousButton.disabled = this.list.scrollLeft <= 1;
      this.nextButton.disabled = this.list.scrollLeft >= maxScroll - 1;
    }
  }

  if (!customElements.get('custom-cart-accessory-upsells')) {
    customElements.define('custom-cart-accessory-upsells', CustomCartAccessoryUpsells);
  }




  // ^^ Keep your scripts inside this IIFE function call to 
  // avoid leaking your variables into the global scope.
})();
